import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  GoogleBusinessProfileDetail,
  GBPProfileSettingsUpdate,
  GBPService,
  GBPProduct,
  GBPCategory
} from '../types/marketing';

// Query keys
export const gbpProfileSettingsKeys = {
  all: ['gbp-profile-settings'] as const,
  detail: (id: number) => [...gbpProfileSettingsKeys.all, id] as const,
  services: (profileId: number) => [...gbpProfileSettingsKeys.all, profileId, 'services'] as const,
  products: (profileId: number) => [...gbpProfileSettingsKeys.all, profileId, 'products'] as const,
  categories: (query: string) => [...gbpProfileSettingsKeys.all, 'categories', query] as const,
};

/**
 * Hook to fetch detailed GBP profile settings
 */
export function useGBPProfileSettings(profileId: number | null) {
  return useQuery({
    queryKey: gbpProfileSettingsKeys.detail(profileId || 0),
    queryFn: async () => {
      if (!profileId) return null;
      const response = await api.get<GoogleBusinessProfileDetail>(
        `/api/marketing/gbp-profiles/${profileId}/profile-settings/`
      );
      return response.data;
    },
    enabled: !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update GBP profile settings locally
 */
export function useUpdateGBPProfileSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      data
    }: {
      profileId: number;
      data: GBPProfileSettingsUpdate;
    }) => {
      const response = await api.patch<GoogleBusinessProfileDetail>(
        `/api/marketing/gbp-profiles/${profileId}/profile-settings/`,
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        gbpProfileSettingsKeys.detail(variables.profileId),
        data
      );
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
    },
  });
}

/**
 * Hook to sync profile data from Google
 */
export function useSyncFromGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId: number) => {
      const response = await api.post<{
        success: boolean;
        message: string;
        warning?: string;
        profile: GoogleBusinessProfileDetail;
      }>(`/api/marketing/gbp-profiles/${profileId}/sync-from-google/`, {});
      return response.data;
    },
    onSuccess: (data, profileId) => {
      if (data.profile) {
        queryClient.setQueryData(
          gbpProfileSettingsKeys.detail(profileId),
          data.profile
        );
      }
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
    },
  });
}

/**
 * Hook to sync profile data to Google
 */
export function useSyncToGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId: number) => {
      const response = await api.post<{
        success: boolean;
        message: string;
        profile: GoogleBusinessProfileDetail;
      }>(`/api/marketing/gbp-profiles/${profileId}/sync-to-google/`, {});
      return response.data;
    },
    onSuccess: (data, profileId) => {
      if (data.profile) {
        queryClient.setQueryData(
          gbpProfileSettingsKeys.detail(profileId),
          data.profile
        );
      }
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
    },
  });
}

/**
 * Hook to search GBP categories
 */
export function useSearchGBPCategories(query: string) {
  return useQuery({
    queryKey: gbpProfileSettingsKeys.categories(query),
    queryFn: async () => {
      if (!query || query.length < 2) return [];
      const response = await api.get<{
        categories: GBPCategory[];
        count: number;
      }>(`/api/marketing/gbp-profiles/categories/?q=${encodeURIComponent(query)}`);
      return response.data.categories;
    },
    enabled: query.length >= 2,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// GBP Services CRUD hooks

/**
 * Hook to fetch GBP services for a profile
 */
export function useGBPServices(profileId: number | null) {
  return useQuery({
    queryKey: gbpProfileSettingsKeys.services(profileId || 0),
    queryFn: async () => {
      if (!profileId) return [];
      const response = await api.get<{ results: GBPService[] }>(
        `/api/marketing/gbp-services/?google_business_profile=${profileId}`
      );
      // Handle both paginated and non-paginated responses
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data.results || [];
    },
    enabled: !!profileId,
  });
}

/**
 * Hook to create a GBP service
 */
export function useCreateGBPService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<GBPService, 'id' | 'created_at' | 'updated_at'>) => {
      const response = await api.post<GBPService>('/api/marketing/gbp-services/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.services(data.google_business_profile)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(data.google_business_profile)
      });
    },
  });
}

/**
 * Hook to update a GBP service
 */
export function useUpdateGBPService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: number;
      data: Partial<GBPService>;
    }) => {
      const response = await api.patch<GBPService>(`/api/marketing/gbp-services/${id}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.services(data.google_business_profile)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(data.google_business_profile)
      });
    },
  });
}

/**
 * Hook to delete a GBP service
 */
export function useDeleteGBPService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, profileId }: { id: number; profileId: number }) => {
      await api.delete(`/api/marketing/gbp-services/${id}/`);
      return { id, profileId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.services(variables.profileId)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(variables.profileId)
      });
    },
  });
}

// GBP Products CRUD hooks

/**
 * Hook to fetch GBP products for a profile
 */
export function useGBPProducts(profileId: number | null) {
  return useQuery({
    queryKey: gbpProfileSettingsKeys.products(profileId || 0),
    queryFn: async () => {
      if (!profileId) return [];
      const response = await api.get<{ results: GBPProduct[] }>(
        `/api/marketing/gbp-products/?google_business_profile=${profileId}`
      );
      // Handle both paginated and non-paginated responses
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data.results || [];
    },
    enabled: !!profileId,
  });
}

/**
 * Hook to create a GBP product
 */
export function useCreateGBPProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<GBPProduct, 'id' | 'created_at' | 'updated_at' | 'image_url'>) => {
      const response = await api.post<GBPProduct>('/api/marketing/gbp-products/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.products(data.google_business_profile)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(data.google_business_profile)
      });
    },
  });
}

/**
 * Hook to update a GBP product
 */
export function useUpdateGBPProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data
    }: {
      id: number;
      data: Partial<GBPProduct>;
    }) => {
      const response = await api.patch<GBPProduct>(`/api/marketing/gbp-products/${id}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.products(data.google_business_profile)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(data.google_business_profile)
      });
    },
  });
}

/**
 * Hook to delete a GBP product
 */
export function useDeleteGBPProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, profileId }: { id: number; profileId: number }) => {
      await api.delete(`/api/marketing/gbp-products/${id}/`);
      return { id, profileId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.products(variables.profileId)
      });
      queryClient.invalidateQueries({
        queryKey: gbpProfileSettingsKeys.detail(variables.profileId)
      });
    },
  });
}
